// A payment split across several bookings keeps only the first one as
// `counterparty`; the full split lives in `lines[]` (one entry per booking,
// or per order line when an allocation targets specific lines). Collapse
// those lines to one entry per booking, in allocation order, so every
// surface lists each booking the payment was attributed to exactly once.
export default function paymentBookingAllocations(payment) {
  const byBooking = new Map();
  (payment?.lines || []).forEach((line) => {
    const bookingId = line?.booking_id ? String(line.booking_id) : '';
    if (!bookingId) return;
    const entry = byBooking.get(bookingId) || {
      booking_id: bookingId,
      booking_reference: line.booking_reference,
      gross_amount: 0,
    };
    const sum = entry.gross_amount + (Number(line.gross_amount) || 0);
    entry.gross_amount = Math.round(sum * 100) / 100;
    if (!entry.booking_reference && line.booking_reference) {
      entry.booking_reference = line.booking_reference;
    }
    byBooking.set(bookingId, entry);
  });
  return [...byBooking.values()];
}

/**
 * IS THERE MONEY ON THIS PAYMENT THAT THIS DOCUMENT DOES NOT ACCOUNT FOR?
 * `null` when there is nothing to say, so the caller renders nothing.
 *
 * Two shapes, both of which mean the allocation still has to be cut:
 *   • `others` — the payment is attributed to bookings this document does not
 *     bill. Named, so the operator can see where the rest went.
 *   • `unallocated` — the payment carries no `lines[]` at all, so nothing
 *     records which order lines it pays, AND it is worth more than this
 *     document bills. The surplus is sitting on the booking unattributed.
 *
 * WHY THIS EXISTS AT ALL. On the invoicing review step the only control
 * beside the payment picker was "+ Record new", so that is what people
 * pressed when the money they were looking at did not line up: 99 duplicate
 * payment rows in production, the same Stripe charge keyed in twice by hand
 * (Jul 50 → Aug 43 → Sep 5 → Oct 0, 2026 — the practice stopped once Stripe
 * ingestion was understood, but the affordance that caused it had not
 * changed). EditAllocationModal already does this job and is used from the
 * Payments page and the order's Financial tab; it was never reachable from
 * the Billingo flow.
 *
 * NOTE WHAT THIS CANNOT SEE. `PaymentService.findByBooking` matches
 * `counterparty.id` OR `lines.booking_id`, so an unsplit payment whose
 * counterparty is a SIBLING booking never reaches this flow's picker at all —
 * it cannot be selected, so no gap can be reported for it. Closing that needs
 * a payment finder on the review step, which is a different change.
 */
export function paymentAllocationGap(payment, { bookingIds = [], gross = null } = {}) {
  if (!payment) return null;
  const mine = new Set((bookingIds || []).filter(Boolean).map(String));
  const allocations = paymentBookingAllocations(payment);
  const others = allocations.filter((a) => !mine.has(a.booking_id));

  const amount = Number(payment.gross_amount) || 0;
  // Only a surplus is worth saying. A payment smaller than the document is
  // the ordinary deposit case and needs no allocation advice.
  const unallocated = allocations.length === 0
    && gross != null && amount - Number(gross) > 0.005;

  if (!others.length && !unallocated) return null;
  return {
    others, unallocated, amount, currency: payment.currency || 'EUR',
  };
}
